import { useCallback, useMemo } from 'react';
import { useParams } from 'next/navigation';
import { mapProduct } from '@/utils/mappers/map-product';
import { Product as SharedProduct } from '@shared/types/product';
import { Locale } from '@/project.config';
import { mapAttribute } from '@/utils/mappers/map-attribute';
import usePath from '@/hooks/usePath';
import useCustomRouter from '@/hooks/useCustomRouter';
import useCart from '@/lib/hooks/useCart';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';

const usePDPProduct = (product: SharedProduct) => {
  const router = useCustomRouter();
  const { path } = usePath();
  // EWM-643 — the removed-from-sale (`GO`) flag is per-country, so the mapper
  // needs the request's real locale (not its `en-AU` default).
  const { locale } = useParams<{ locale: Locale }>();

  const { selectedBusinessUnit, selectedStore } = useStoreAndBusinessUnits();

  const { cart } = useCart(selectedBusinessUnit?.key, selectedStore?.key);

  //   Update the currentVariantIndex to use the sku from the path
  const currentVariantIndex = product.variants.findIndex((v) => v.sku === path.split('/')[3]);

  const onChangeVariant = useCallback(
    (variant: 'color' | 'model', value: string) => {
      const updatedVariantSKU = product.variants.find((v) =>
        v.attributes?.[variant].key ? v.attributes?.[variant].key === value : v.attributes?.[variant] === value,
      )?.sku;

      router.replace(`/slug/p/${updatedVariantSKU}`);
    },
    [product.variants, router],
  );

  // EWM-195 — direct SKU-level variant change for the Dan-style dropdown.
  // Mirrors `onChangeVariant` but takes the target SKU verbatim.
  const onChangeSKU = useCallback(
    (sku: string) => {
      router.replace(`/slug/p/${sku}`);
    },
    [router],
  );

  const mappedProduct = useMemo(
    () => mapProduct(product, { variantIndex: currentVariantIndex, cart, locale, filterOutNonMatchingVariants: false }),
    [product, currentVariantIndex, cart, locale],
  );

  const currentColor = useMemo(() => {
    const variant = product.variants[currentVariantIndex];
    return variant.attributes?.color ? mapAttribute(variant.attributes.color) : undefined;
  }, [product, currentVariantIndex]);

  const currentSpecs = useMemo(() => {
    const variant = product.variants[currentVariantIndex];
    return variant.attributes?.model
      ? mapAttribute({ label: variant.attributes.model, key: variant.attributes.model })
      : undefined;
  }, [product, currentVariantIndex]);

  return {
    currentColor,
    currentSpecs,
    mappedProduct,
    onChangeVariant,
    onChangeSKU,
  };
};

export default usePDPProduct;
